/**
 * App Router
 *
 * Main routing configuration for Stagecraft.
 * Handles public pages (landing, login, support) and protected pages (CAD app).
 * All providers (Redux, Theme, Auth) are wrapped here so main.tsx stays minimal.
 */

import React, { Suspense, lazy, useEffect, useState } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { ThemeProvider } from "./core/theme/ThemeContext";
import { AuthProvider } from "./core/auth";
import { ProtectedRoute } from "./website/components/ProtectedRoute";
import { AdminRoute } from "./website/components/AdminRoute";
import { CookieConsent, WelcomeAnimation } from "./website/components";

// Lazy-load all website pages for code-splitting
const LandingPage = lazy(() => import("./website/pages/LandingPage"));
const FeedbackHelpPage = lazy(() => import("./website/pages/FeedbackHelpPage"));
const LoginPage = lazy(() => import("./website/pages/LoginPage"));
const AuthConfirmPage = lazy(() => import("./website/pages/AuthConfirmPage"));
const PolicyPage = lazy(() => import("./website/pages/PolicyPage"));
const DonationPage = lazy(() => import("./website/pages/DonationPage"));
const ThankYouPage = lazy(() => import("./website/pages/ThankYouPage"));
const AccountPage = lazy(() => import("./website/pages/AccountPage"));
const ComingSoonPage = lazy(() => import("./website/pages/ComingSoonPage"));
const AdminPage = lazy(() => import("./website/pages/admin/AdminPage"));
const AdminUserDetailPage = lazy(
  () => import("./website/pages/admin/AdminUserDetailPage"),
);

/**
 * Lazy-load the CAD application — and, with it, the entire CAD engine (WR-486).
 *
 * ⛔ THE THREE AWAITS BELOW ARE ORDERED, AND THE ORDER IS THE POINT.
 *
 *  1. `./cad/cadBoot` — importing it RUNS its module body: `initializeZUpSystem()` before
 *     any Three.js object exists, then `CameraControlsImpl.install({ THREE })`. Because
 *     this happens before step 2, every Three object `App`'s module graph creates is born
 *     Z-up, exactly as when these calls lived in `main.tsx`.
 *  2. `./cad/CadRoot` — evaluates the app's graph (`@/App` and its 1,000+ modules).
 *  3. `cadBootReady` — the async half of boot: migrations, the persisted-state load, and
 *     `initializeContainer(store)`, which itself awaits a dynamic `SelectTool` import
 *     (its own 190.70 kB chunk). `InteractionLayer.tsx:88` resolves `DI_TOKENS.SelectTool`
 *     in a mount effect whose `catch` only logs, so rendering before the container is
 *     populated yields a CAD app with no select tool and NO ERROR. Awaiting here is what
 *     prevents that; a bare side-effect import could not.
 *
 * The promise created at step 1 is in flight while step 2 downloads, so the awaits are
 * ordered without being serialised. React caches both the promise and its resolved value
 * ("React will not call `load` more than once"), so StrictMode's double-invoke cannot
 * double-boot.
 *
 * @see architecture/Website/entry-split-s1/stage4-proposal-v4.md §1.3
 */
const CadApp = lazy(async () => {
  const { cadBootReady } = await import("./cad/cadBoot");
  const mod = await import("./cad/CadRoot");
  await cadBootReady;
  return mod;
});

// Loading component for lazy-loaded routes
function LoadingScreen() {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        backgroundColor: "#000000",
        color: "#a1a1aa",
        fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
      }}
    >
      Loading Stagecraft...
    </div>
  );
}

/** Reset scroll position on route change */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

/** /preview route handler — sets session flag and redirects to landing */
function PreviewUnlock() {
  localStorage.setItem("stagecraft_preview", "true");
  return <Navigate to="/" replace />;
}

/**
 * Pre-launch gate. Shows ComingSoonPage to the public.
 * Bypass: visit /preview once to unlock the full site (persists in localStorage).
 * Disable: set VITE_COMING_SOON=false or remove the env var.
 */
function usePreviewGate(): boolean {
  const [unlocked, setUnlocked] = useState(() => {
    // Gate is off unless VITE_COMING_SOON is explicitly 'true'
    if (import.meta.env.VITE_COMING_SOON !== "true") return true;
    return localStorage.getItem("stagecraft_preview") === "true";
  });

  useEffect(() => {
    // Listen for unlock from another tab or late /preview navigation
    const handler = () => {
      if (localStorage.getItem("stagecraft_preview") === "true") {
        setUnlocked(true);
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  return unlocked;
}

function AppRoutes() {
  const unlocked = usePreviewGate();

  if (!unlocked) {
    return (
      <Routes>
        <Route path="/preview" element={<PreviewUnlock />} />
        <Route
          path="*"
          element={
            <Suspense fallback={<LoadingScreen />}>
              <ComingSoonPage />
            </Suspense>
          }
        />
      </Routes>
    );
  }

  return (
    <>
      <CookieConsent />
      <WelcomeAnimation />
      <Routes>
        {/* Preview unlock route (no-op when already unlocked) */}
        <Route path="/preview" element={<PreviewUnlock />} />

        {/* Public routes */}
        <Route
          path="/"
          element={
            <Suspense fallback={<LoadingScreen />}>
              <LandingPage />
            </Suspense>
          }
        />
        <Route
          path="/login"
          element={
            <Suspense fallback={<LoadingScreen />}>
              <LoginPage />
            </Suspense>
          }
        />
        {/*
          Magic-link confirmation interstitial (WR-482(b)).
          MUST stay outside ProtectedRoute: the visitor is not authenticated yet —
          spending the token on this page is what authenticates them. Wrapping it
          in ProtectedRoute would bounce every sign-in link to /login.
        */}
        <Route
          path="/auth/confirm"
          element={
            <Suspense fallback={<LoadingScreen />}>
              <AuthConfirmPage />
            </Suspense>
          }
        />
        <Route
          path="/feedback"
          element={
            <Suspense fallback={<LoadingScreen />}>
              <FeedbackHelpPage />
            </Suspense>
          }
        />
        <Route
          path="/feedback/*"
          element={
            <Suspense fallback={<LoadingScreen />}>
              <FeedbackHelpPage />
            </Suspense>
          }
        />

        {/* Donation / Support pages */}
        <Route
          path="/support"
          element={
            <Suspense fallback={<LoadingScreen />}>
              <DonationPage />
            </Suspense>
          }
        />
        <Route
          path="/support/thank-you"
          element={
            <Suspense fallback={<LoadingScreen />}>
              <ThankYouPage />
            </Suspense>
          }
        />
        <Route
          path="/support/perks"
          element={<Navigate to="/account" replace />}
        />
        <Route
          path="/support/terms"
          element={
            <Suspense fallback={<LoadingScreen />}>
              <PolicyPage
                contentUrl="/Terms/DonationTerms.md"
                title="Donation Terms"
                backLink={{ to: "/support", label: "Back to Donation Page" }}
              />
            </Suspense>
          }
        />

        {/* Account page (handles its own auth guard) */}
        <Route
          path="/account"
          element={
            <Suspense fallback={<LoadingScreen />}>
              <AccountPage />
            </Suspense>
          }
        />

        {/* Admin dashboard (admin role required) */}
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <Suspense fallback={<LoadingScreen />}>
                <AdminPage />
              </Suspense>
            </AdminRoute>
          }
        />
        <Route
          path="/admin/user/:userId"
          element={
            <AdminRoute>
              <Suspense fallback={<LoadingScreen />}>
                <AdminUserDetailPage />
              </Suspense>
            </AdminRoute>
          }
        />

        {/* Legal pages */}
        <Route
          path="/privacy"
          element={
            <Suspense fallback={<LoadingScreen />}>
              <PolicyPage
                contentUrl="/Terms/Privacy.md"
                title="Privacy Policy"
              />
            </Suspense>
          }
        />
        <Route
          path="/terms"
          element={
            <Suspense fallback={<LoadingScreen />}>
              <PolicyPage contentUrl="/Terms/TOS.md" title="Terms of Service" />
            </Suspense>
          }
        />

        {/* Protected CAD app */}
        <Route
          path="/app"
          element={
            <Suspense fallback={<LoadingScreen />}>
              <ProtectedRoute>
                <CadApp />
              </ProtectedRoute>
            </Suspense>
          }
        />
        <Route
          path="/app/*"
          element={
            <Suspense fallback={<LoadingScreen />}>
              <ProtectedRoute>
                <CadApp />
              </ProtectedRoute>
            </Suspense>
          }
        />

        {/* Backward-compat redirects from old /site prefix */}
        <Route path="/site" element={<Navigate to="/" replace />} />
        <Route
          path="/site/feedback"
          element={<Navigate to="/feedback" replace />}
        />
        <Route
          path="/site/feedback/*"
          element={<Navigate to="/feedback" replace />}
        />
        <Route
          path="/site/support"
          element={<Navigate to="/support" replace />}
        />
        <Route
          path="/site/support/thank-you"
          element={<Navigate to="/support/thank-you" replace />}
        />
        <Route
          path="/site/support/perks"
          element={<Navigate to="/account" replace />}
        />
        <Route
          path="/site/account"
          element={<Navigate to="/account" replace />}
        />
        <Route
          path="/site/support/terms"
          element={<Navigate to="/support/terms" replace />}
        />

        {/* Catch-all — unknown URLs go to landing */}
        <Route
          path="*"
          element={
            <Suspense fallback={<LoadingScreen />}>
              <LandingPage />
            </Suspense>
          }
        />
      </Routes>
    </>
  );
}

/**
 * The app shell every visitor gets — website and CAD alike.
 *
 * `<Provider store>` and `<CommandProcessorProvider>` USED to wrap this tree. They moved
 * to `src/cad/CadRoot.tsx`, behind the `/app` boundary, because they carried the Redux
 * store (327 files / 4.0 MB, reaching `three` and `src/core/rendering`) into the payload
 * every marketing visitor downloads — and nothing above the boundary consumed them:
 * measured, 0 of 44 files under `src/website/` reference Redux or the command processor,
 * against a control of 17 that use `useState`. Arm 10 of
 * `src/__tests__/architecture/websiteEntryExcludesCadEngine.test.ts` keeps it that way,
 * because after this change a website component calling `useSelector` is no longer a
 * harmless oddity — with no ErrorBoundary above this router, it is a white screen.
 */
export function AppRouter() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default AppRouter;
